import React, { useState, useEffect } from 'react';
import apiFetch from '../../../../utils/api';
import { useStats } from '../../../../context/StatsContext';
import './CreateAccounts.css';

// ── Pakistan-specific validation helpers ──
const validators = {
  username: (v) => {
    if (!v) return 'Username is required.';
    if (v.length < 3) return 'Username must be at least 3 characters.';
    if (v.length > 30) return 'Username must be 30 characters or less.';
    if (!/^[a-zA-Z0-9_]+$/.test(v)) return 'Only letters, numbers, and underscores allowed.';
    return '';
  },
  email: (v) => {
    if (!v) return 'Email is required.';
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v)) return 'Enter a valid email (e.g., name@gmail.com).';
    return '';
  },
  accountNumber: (v, method) => {
    if (!v) return 'Account number is required.';
    if (method === 'EasyPaisa' || method === 'JazzCash') {
      if (!/^03\d{9}$/.test(v)) return 'Must be 11 digits starting with 03 (e.g., 03001234567).';
    } else if (method === 'Bank') {
      // Pakistani IBAN: PK + 2 check digits + 4 bank code + 16 account = 24 chars
      const cleaned = v.replace(/[\s-]/g, '');
      if (cleaned.toUpperCase().startsWith('PK')) {
        if (!/^PK\d{2}[A-Z]{4}\d{16}$/.test(cleaned.toUpperCase())) return 'Invalid IBAN. Format: PK36SCBL0000001123456702 (24 characters).';
      } else {
        if (cleaned.length < 10 || cleaned.length > 20) return 'Bank account number must be 10-20 digits.';
        if (!/^\d+$/.test(cleaned)) return 'Bank account number must contain only digits.';
      }
    }
    return '';
  },
  accountTitle: (v) => {
    if (!v) return 'Account title is required.';
    if (v.length < 3) return 'Account title must be at least 3 characters.';
    if (!/^[a-zA-Z\s.'-]+$/.test(v)) return 'Account title should contain only letters and spaces.';
    return '';
  },
  paymentMethod: (v) => {
    if (!v) return 'Please select a payment method.';
    if (!['EasyPaisa', 'JazzCash', 'Bank'].includes(v)) return 'Invalid payment method.';
    return '';
  },
  pin: (v) => {
    if (!v) return 'Please select an E-Pin.';
    return '';
  },
};

const CreateAccounts = () => {
  const { pins: allPins, refreshAll } = useStats();
  const pinsArray = allPins && Array.isArray(allPins.pins) ? allPins.pins : (Array.isArray(allPins) ? allPins : []);
  const pins = pinsArray.filter(p => p.status === 'Available');
  const [treeMembers, setTreeMembers] = useState([]);
  const [formData, setFormData] = useState({
    pin: '',
    username: '',
    email: '',
    accountNumber: '',
    paymentMethod: '',
    accountTitle: '',
    underUserId: '',
    position: 'Right',
    agreeTerms: false,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitStatus, setSubmitStatus] = useState({ message: '', error: false });
  const [submitting, setSubmitting] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    apiFetch('/api/tree/members')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setTreeMembers(data);
        else setTreeMembers([]);
      })
      .catch(() => setTreeMembers([]));
  }, []);

  // Validate a single field
  const validateField = (name, value, allData = formData) => {
    if (name === 'accountNumber') return validators.accountNumber(value, allData.paymentMethod);
    if (validators[name]) return validators[name](value);
    return '';
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newData = { ...formData, [name]: type === 'checkbox' ? checked : value };

    // Auto-set position when selecting a tree member
    if (name === 'underUserId' && value) {
      const member = treeMembers.find(m => m.username === value);
      if (member && member.availablePositions.length === 1) {
        newData.position = member.availablePositions[0] === 'left' ? 'Left' : 'Right';
      }
    }

    // Re-validate accountNumber when paymentMethod changes
    if (name === 'paymentMethod' && newData.accountNumber) {
      setFieldErrors(prev => ({ ...prev, accountNumber: validators.accountNumber(newData.accountNumber, value) }));
    }

    setFormData(newData);

    // Validate on change if field was already touched
    if (touched[name] && validators[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: validateField(name, type === 'checkbox' ? checked : value, newData) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    if (validators[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ message: '', error: false });

    // Validate ALL fields
    const errors = {};
    const fieldsToValidate = ['pin', 'username', 'email', 'accountNumber', 'accountTitle', 'paymentMethod'];
    fieldsToValidate.forEach(name => {
      const err = validateField(name, formData[name]);
      if (err) errors[name] = err;
    });

    if (!formData.agreeTerms) errors.agreeTerms = 'You must accept the terms.';

    // Mark all as touched
    const allTouched = {};
    fieldsToValidate.forEach(n => allTouched[n] = true);
    setTouched(allTouched);
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Find first error message
      const firstErr = Object.values(errors)[0];
      setSubmitStatus({ message: firstErr, error: true });
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiFetch('/api/accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      setSubmitStatus({ message: data.message || 'Account created!', error: !data.success });

      if (data.success) {
        setFormData(f => ({ ...f, pin: '', username: '', email: '' }));
        setFieldErrors({});
        setTouched({});
        refreshAll();
      }
    } catch {
      setSubmitStatus({ message: 'Server error. Please try again.', error: true });
    }
    setSubmitting(false);
  };

  // Helper to show field error
  const FieldError = ({ name }) => {
    if (!touched[name] || !fieldErrors[name]) return null;
    return <div className="ca-field-error">{fieldErrors[name]}</div>;
  };

  // Dynamic placeholder based on payment method
  const getAccountPlaceholder = () => {
    if (formData.paymentMethod === 'EasyPaisa' || formData.paymentMethod === 'JazzCash') return '03001234567';
    if (formData.paymentMethod === 'Bank') return 'PK36SCBL0000001123456702 or 10-20 digit account #';
    return 'Select payment method first';
  };

  return (
    <div className="ca-page">
      <div className="ca-header">
        <h1 className="ca-header__title">Join a User</h1>
        <p className="ca-header__sub">Use an E-Pin to activate a new account in your binary tree</p>
      </div>

      <div className="ca-layout">
        {/* Info Card */}
        <div className="ca-info">
          <div className="ca-info__card">
            <div className="ca-info__icon">🔑</div>
            <h3>How It Works</h3>
            <ol className="ca-info__steps">
              <li>Get an E-Pin from the "Request E-Pins" page</li>
              <li>Wait for admin to approve your request</li>
              <li>Select your pin below to activate a new user</li>
              <li>Choose where to place them in your tree (Left/Right)</li>
            </ol>
          </div>
          <div className="ca-info__pins">
            <div className="ca-info__pins-label">Available Pins</div>
            <div className="ca-info__pins-count">{pins.length}</div>
            {pins.length === 0 && (
              <p className="ca-info__pins-hint">No pins available. Request one first!</p>
            )}
          </div>

          {/* Validation Rules Info */}
          <div className="ca-info__card ca-req-card">
            <h4 className="ca-req-title">📋 Field Requirements</h4>
            <ul className="ca-req-list">
              <li><strong>Username:</strong> 3-30 chars, letters/numbers/underscores only</li>
              <li><strong>Email:</strong> Valid format (e.g., name@gmail.com)</li>
              <li><strong>EasyPaisa/JazzCash:</strong> 11-digit number starting with 03</li>
              <li><strong>Bank Account:</strong> Pakistani IBAN (PK + 22 chars) or 10-20 digit account number</li>
              <li><strong>Account Title:</strong> Full name as on bank account</li>
            </ul>
          </div>
        </div>

        {/* Form Card */}
        <div className="ca-form-card">
          <h2 className="ca-form-card__title">Create Account</h2>
          <p className="ca-form-card__urdu" dir="rtl">
            تمام ڈیٹا تسلی سے لکھ کر چیک کریں اور پھر کریٹ اکاؤنٹ پر کلک کریں۔
          </p>

          {submitStatus.message && (
            <div className={`ca-alert ${submitStatus.error ? 'ca-alert--error' : 'ca-alert--success'}`}>
              {submitStatus.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="ca-form" noValidate>
            {/* Pin Select */}
            <div className="ca-field">
              <label className="ca-label">E-Pin <span className="ca-required">*</span></label>
              <select name="pin" value={formData.pin} onChange={handleChange} onBlur={handleBlur} className={`ca-select ${touched.pin && fieldErrors.pin ? 'ca-input--error' : ''}`} required>
                <option value="">— Select an E-Pin —</option>
                {pins.map(p => (
                  <option key={p.id} value={p.code}>{p.code}</option>
                ))}
              </select>
              <FieldError name="pin" />
            </div>

            <div className="ca-row">
              <div className="ca-field">
                <label className="ca-label">Username <span className="ca-required">*</span></label>
                <div className={`ca-input-wrap ${touched.username && fieldErrors.username ? 'ca-input-wrap--error' : touched.username && !fieldErrors.username ? 'ca-input-wrap--valid' : ''}`}>
                  <span className="ca-input-icon">👤</span>
                  <input type="text" name="username" value={formData.username} placeholder="3-30 chars, letters/numbers/_" onChange={handleChange} onBlur={handleBlur} required />
                </div>
                <FieldError name="username" />
              </div>
              <div className="ca-field">
                <label className="ca-label">Email <span className="ca-required">*</span></label>
                <div className={`ca-input-wrap ${touched.email && fieldErrors.email ? 'ca-input-wrap--error' : touched.email && !fieldErrors.email ? 'ca-input-wrap--valid' : ''}`}>
                  <span className="ca-input-icon">📧</span>
                  <input type="email" name="email" value={formData.email} placeholder="name@gmail.com" onChange={handleChange} onBlur={handleBlur} required />
                </div>
                <FieldError name="email" />
              </div>
            </div>

            <div className="ca-row">
              <div className="ca-field">
                <label className="ca-label">Payment Method <span className="ca-required">*</span></label>
                <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange} onBlur={handleBlur} className={`ca-select ${touched.paymentMethod && fieldErrors.paymentMethod ? 'ca-input--error' : ''}`} required>
                  <option value="">Select method</option>
                  <option value="EasyPaisa">EasyPaisa</option>
                  <option value="JazzCash">JazzCash</option>
                  <option value="Bank">Bank Transfer</option>
                </select>
                <FieldError name="paymentMethod" />
              </div>
              <div className="ca-field">
                <label className="ca-label">Account Number <span className="ca-required">*</span></label>
                <div className={`ca-input-wrap ${touched.accountNumber && fieldErrors.accountNumber ? 'ca-input-wrap--error' : touched.accountNumber && !fieldErrors.accountNumber ? 'ca-input-wrap--valid' : ''}`}>
                  <span className="ca-input-icon">🏦</span>
                  <input type="text" name="accountNumber" placeholder={getAccountPlaceholder()} value={formData.accountNumber} onChange={handleChange} onBlur={handleBlur} required />
                </div>
                <FieldError name="accountNumber" />
              </div>
            </div>

            <div className="ca-row">
              <div className="ca-field">
                <label className="ca-label">Account Title <span className="ca-required">*</span></label>
                <div className={`ca-input-wrap ${touched.accountTitle && fieldErrors.accountTitle ? 'ca-input-wrap--error' : touched.accountTitle && !fieldErrors.accountTitle ? 'ca-input-wrap--valid' : ''}`}>
                  <span className="ca-input-icon">💼</span>
                  <input type="text" name="accountTitle" placeholder="Full name as on bank account" value={formData.accountTitle} onChange={handleChange} onBlur={handleBlur} required />
                </div>
                <FieldError name="accountTitle" />
              </div>
              <div className="ca-field" style={{ position: 'relative' }}>
                <label className="ca-label">Place Under</label>
                <div className="ca-input-wrap">
                  <input
                    type="text"
                    name="underUserId"
                    value={formData.underUserId}
                    onChange={handleChange}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                    className="ca-select"
                    placeholder="— You (direct) — (Type to search)"
                    autoComplete="off"
                  />
                </div>
                {searchFocused && !treeMembers.find(m => m.username === formData.underUserId) && (
                  <ul className="ca-search-dropdown">
                    {treeMembers.filter(m => m.username.toLowerCase().includes(formData.underUserId.toLowerCase()) || m.email.toLowerCase().includes(formData.underUserId.toLowerCase())).map(m => (
                      <li key={m.username} className="ca-search-item"
                        onMouseDown={() => handleChange({ target: { name: 'underUserId', value: m.username, type: 'text' } })}>
                        {m.username} <span className="ca-search-item-meta">{m.availablePositions.length > 0 ? `(${m.availablePositions.join(' & ')} open)` : '(full)'}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Position */}
            <div className="ca-field">
              <label className="ca-label">Position in Tree</label>
              <div className="ca-position-row">
                <label className={`ca-pos-btn ${formData.position === 'Left' ? 'ca-pos-btn--active' : ''}`}>
                  <input type="radio" name="position" value="Left" onChange={handleChange} />
                  ◀ Left
                </label>
                <label className={`ca-pos-btn ${formData.position === 'Right' ? 'ca-pos-btn--active' : ''}`}>
                  <input type="radio" name="position" value="Right" defaultChecked onChange={handleChange} />
                  Right ▶
                </label>
              </div>
            </div>

            {/* Terms */}
            <label className={`ca-terms ${touched.agreeTerms && fieldErrors.agreeTerms ? 'ca-terms--error' : ''}`}>
              <input type="checkbox" name="agreeTerms" onChange={handleChange} />
              <span>I accept the Terms and Conditions <span className="ca-required">*</span></span>
            </label>

            <button type="submit" className="ca-submit" disabled={submitting || pins.length === 0}>
              {submitting ? 'Creating...' : pins.length === 0 ? 'No Pins Available' : '🔑 Create Account with E-Pin'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateAccounts;